import React, { useEffect, useState } from 'react';
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';
import { getConfig } from '@edx/frontend-platform';
import {
  Form, Spinner, Alert, Button, Row, Col,
} from '@openedx/paragon';
import { logStudioHomeError } from '../../../logging';

type Props = {
  orgSlug: string;
  orgName?: string;
};

type EmailConfig = {
  orgSlug: string;
  orgName: string;
  numberOfDaysUntilEmail: {
    first: number;
    second: number;
    third: number;
  };
  sendgridTemplates: {
    week_one: string;
    week_two: string;
    week_three: string;
  };
  optOut: boolean;
};

const InactivityOptOutToggle = ({
  orgSlug,
  orgName = '',
}: Props) => {
  const [optOut, setOptOut] = useState<boolean>(false);
  const [daysFirst, setDaysFirst] = useState<number>(10);
  const [daysSecond, setDaysSecond] = useState<number>(5);
  const [daysThird, setDaysThird] = useState<number>(6);
  const [templateWeekOne, setTemplateWeekOne] = useState<string>('d-3f9da37b6e7648869b9686fa753bea94');
  const [templateWeekTwo, setTemplateWeekTwo] = useState<string>('d-c5b9334c6629498d8f6de87ec4a85c0b');
  const [templateWeekThree, setTemplateWeekThree] = useState<string>('d-633f3fa23ab84d01964e94826f06fa26');

  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const apiBaseUrl = getConfig().LMS_BASE_URL;
  // Use orgSlug as fallback for orgName if not provided
  const effectiveOrgName = orgName || orgSlug;

  useEffect(() => {
    let mounted = true;
    async function init() {
      setInitialLoading(true);
      try {
        const client = getAuthenticatedHttpClient();
        const response = await client.get(
          `${apiBaseUrl}/alias/inactivity-emails`,
          {
            params: { org_slug: orgSlug, org_name: effectiveOrgName },
          },
        );
        const configData = response.data as EmailConfig;

        if (mounted) {
          setOptOut(configData.optOut || false);
          setDaysFirst(configData.numberOfDaysUntilEmail?.first || 10);
          setDaysSecond(configData.numberOfDaysUntilEmail?.second || 5);
          setDaysThird(configData.numberOfDaysUntilEmail?.third || 6);
          setTemplateWeekOne(configData.sendgridTemplates?.week_one || 'd-3f9da37b6e7648869b9686fa753bea94');
          setTemplateWeekTwo(configData.sendgridTemplates?.week_two || 'd-c5b9334c6629498d8f6de87ec4a85c0b');
          setTemplateWeekThree(configData.sendgridTemplates?.week_three || 'd-633f3fa23ab84d01964e94826f06fa26');
        }
      } catch (err) {
        logStudioHomeError('cmp_inactivity_config_fetch_failed', err);
        // Silently fail on initial load - use defaults
      } finally {
        if (mounted) {
          setInitialLoading(false);
        }
      }
    }
    if (orgSlug) {
      init();
    }
    return () => {
      mounted = false;
    };
  }, [orgSlug, effectiveOrgName, apiBaseUrl]);

  const validateDays = (): boolean => {
    // All values must be at least 1
    if (daysFirst < 1 || daysSecond < 1 || daysThird < 1) {
      return false;
    }
    return true;
  };

  async function handleSave() {
    // Validate days before saving
    if (!validateDays()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const client = getAuthenticatedHttpClient();

      // Build the complete config object with all fields
      const updatedConfig: EmailConfig = {
        orgSlug,
        orgName: effectiveOrgName,
        numberOfDaysUntilEmail: {
          first: daysFirst,
          second: daysSecond,
          third: daysThird,
        },
        sendgridTemplates: {
          week_one: templateWeekOne,
          week_two: templateWeekTwo,
          week_three: templateWeekThree,
        },
        optOut,
      };

      await client.put(`${apiBaseUrl}/alias/inactivity-emails`, updatedConfig);
      setSuccessMessage('Configuration saved successfully!');
      setHasChanges(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (e: any) {
      const errorMessage = e?.response?.data?.error || e?.message || 'Failed to update settings';
      setError(errorMessage);
      logStudioHomeError('cmp_inactivity_config_update_failed', e);
    } finally {
      setLoading(false);
    }
  }

  const handleSaveClick = () => { handleSave(); };

  const handleFieldChange = () => {
    setHasChanges(true);
    setError(null);
    setSuccessMessage(null);
  };

  if (initialLoading) {
    return (
      <div className="d-flex align-items-center">
        <Spinner animation="border" size="sm" className="mr-2" />
        <small className="text-muted">Loading configuration...</small>
      </div>
    );
  }

  return (
    <div className="inactivity-email-config">
      {successMessage && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <strong>Error:</strong> {error}
        </Alert>
      )}

      <Form>
        <Form.Group className="mb-4">
          <Form.Checkbox
            checked={optOut}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setOptOut(e.target.checked);
              handleFieldChange();
            }}
            disabled={loading}
          >
            <strong>Opt out of inactivity emails</strong>
          </Form.Checkbox>
          <Form.Text className="text-muted">
            When enabled, learners in this organization will not receive
            automated inactivity reminder emails.
          </Form.Text>
        </Form.Group>

        <h5 className="mb-3">Email Campaign Schedule</h5>
        <p className="text-muted small mb-3">
          Configure the email sequence: First email is sent after days of inactivity.
          Second and third emails are sent after the specified number of days from the previous email.
        </p>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>First Email (days)</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={daysFirst}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setDaysFirst(parseInt(e.target.value, 10) || 0);
                  handleFieldChange();
                }}
                disabled={loading}
                isInvalid={daysFirst < 1}
              />
              <Form.Text className="text-muted">
                Days of inactivity before first email
                {daysFirst < 1 && (
                  <span className="text-danger"> (must be at least 1)</span>
                )}
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Second Email (days)</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={daysSecond}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setDaysSecond(parseInt(e.target.value, 10) || 0);
                  handleFieldChange();
                }}
                disabled={loading}
                isInvalid={daysSecond < 1}
              />
              <Form.Text className="text-muted">
                Days after first email
                {daysSecond < 1 && (
                  <span className="text-danger"> (must be at least 1)</span>
                )}
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Third Email (days)</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={daysThird}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setDaysThird(parseInt(e.target.value, 10) || 0);
                  handleFieldChange();
                }}
                disabled={loading}
                isInvalid={daysThird < 1}
              />
              <Form.Text className="text-muted">
                Days after second email
                {daysThird < 1 && (
                  <span className="text-danger"> (must be at least 1)</span>
                )}
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <h5 className="mb-3 mt-4">SendGrid Template IDs</h5>
        <p className="text-muted small mb-3">
          Configure the SendGrid template IDs for each reminder email.
        </p>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Week One Template</Form.Label>
              <Form.Control
                type="text"
                value={templateWeekOne}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setTemplateWeekOne(e.target.value);
                  handleFieldChange();
                }}
                disabled={loading}
                placeholder="d-xxxxxxxxxxxxxxxxxxxxxxxx"
              />
              <Form.Text className="text-muted">First email template</Form.Text>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Week Two Template</Form.Label>
              <Form.Control
                type="text"
                value={templateWeekTwo}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setTemplateWeekTwo(e.target.value);
                  handleFieldChange();
                }}
                disabled={loading}
                placeholder="d-xxxxxxxxxxxxxxxxxxxxxxxx"
              />
              <Form.Text className="text-muted">
                Second email template
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Week Three Template</Form.Label>
              <Form.Control
                type="text"
                value={templateWeekThree}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setTemplateWeekThree(e.target.value);
                  handleFieldChange();
                }}
                disabled={loading}
                placeholder="d-xxxxxxxxxxxxxxxxxxxxxxxx"
              />
              <Form.Text className="text-muted">Third email template</Form.Text>
            </Form.Group>
          </Col>
        </Row>

        <div className="d-flex align-items-center mt-4">
          <Button
            variant="primary"
            onClick={handleSaveClick}
            disabled={loading || !hasChanges}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="mr-2" />
                Saving...
              </>
            ) : (
              'Save Configuration'
            )}
          </Button>
          {hasChanges && !loading && (
            <small className="text-warning ml-3">
              You have unsaved changes
            </small>
          )}
        </div>
      </Form>
    </div>
  );
};

export default InactivityOptOutToggle;
